import React from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { CREATE_JOB_POSITION } from "../../utils/constants";
import useRequireAuth from "../../utils/useRequireAuth";
import { useNavigate, Link } from "react-router-dom";
import LayoutHOC from "../LayoutHOC";

const CreateJobPosition = () => {
  const user = useRequireAuth();
  const navigate = useNavigate();

  const initialValues = {
    name: "",
    name_hindi: "",
    name_marathi: "",
    name_punjabi: "",
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string().required("English name is required"),
  });

  const handleSubmit = async (values, { setSubmitting, setFieldError }) => {
    try {
      const Authtoken = user.token;
      const formDataWithFile = new FormData();

      formDataWithFile.append("name_english", values.name);
      formDataWithFile.append("name_hindi", values.name_hindi);
      formDataWithFile.append("name_marathi", values.name_marathi);
      formDataWithFile.append("name_punjabi", values.name_punjabi);

      const response = await fetch(CREATE_JOB_POSITION, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${Authtoken}`,
        },
        body: formDataWithFile,
      });

      if (response.ok) {
        navigate(`/jobs_position/`);
      }
      setSubmitting(false);
    } catch (error) {
      setFieldError("form", "An error occurred while submitting the form.");
      setSubmitting(false);
    }
  };

  return (
    <main id="maincontent">
      <div className="p-6 mt-14 min-h-screen bg-gradient-to-br from-slate-50 to-pink-50">
        <div className="mb-6">
          <div className="flex items-center gap-3">
            <Link to="/jobs_position" className="p-2 hover:bg-white rounded-lg transition-colors">
              <i className="bi bi-arrow-left text-gray-600 text-xl"></i>
            </Link>
            <div className="p-3 bg-gradient-to-br from-pink-500 to-rose-600 rounded-xl shadow-lg">
              <i className="bi bi-plus-lg text-white text-2xl"></i>
            </div>
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
                Add New Job Position
              </h1>
              <p className="text-gray-500">Create a new job position with multilingual names</p>
            </div>
          </div>
        </div>

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting }) => (
            <Form>
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="p-6 border-b border-gray-100">
                  <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                    <i className="bi bi-translate text-pink-500"></i>
                    Job Position Names in Multiple Languages
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">
                    Enter the job position name in all supported languages. English is required.
                  </p>
                </div>

                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Field name="name">
                      {({ field }) => (
                        <div>
                          <label className="flex items-center gap-2 mb-2 text-sm font-medium text-gray-700">
                            <span className="w-6 h-6 bg-blue-100 rounded flex items-center justify-center text-xs font-bold text-blue-600">EN</span>
                            English Name
                            <span className="text-red-500">*</span>
                          </label>
                          <input
                            {...field}
                            type="text"
                            placeholder="Enter job position in English"
                            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-pink-500 focus:border-pink-500 transition-all text-gray-700"
                          />
                          <ErrorMessage name="name" component="div" className="mt-1 text-red-500 text-sm" />
                        </div>
                      )}
                    </Field>

                    <Field name="name_hindi">
                      {({ field }) => (
                        <div>
                          <label className="flex items-center gap-2 mb-2 text-sm font-medium text-gray-700">
                            <span className="w-6 h-6 bg-orange-100 rounded flex items-center justify-center text-xs font-bold text-orange-600">HI</span>
                            Hindi Name
                          </label>
                          <input
                            {...field}
                            type="text"
                            placeholder="हिंदी में पद का नाम दर्ज करें"
                            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-pink-500 focus:border-pink-500 transition-all text-gray-700"
                          />
                          <ErrorMessage name="name_hindi" component="div" className="mt-1 text-red-500 text-sm" />
                        </div>
                      )}
                    </Field>

                    <Field name="name_marathi">
                      {({ field }) => (
                        <div>
                          <label className="flex items-center gap-2 mb-2 text-sm font-medium text-gray-700">
                            <span className="w-6 h-6 bg-green-100 rounded flex items-center justify-center text-xs font-bold text-green-600">MR</span>
                            Marathi Name
                          </label>
                          <input
                            {...field}
                            type="text"
                            placeholder="मराठीत पदाचे नाव प्रविष्ट करा"
                            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-pink-500 focus:border-pink-500 transition-all text-gray-700"
                          />
                          <ErrorMessage name="name_marathi" component="div" className="mt-1 text-red-500 text-sm" />
                        </div>
                      )}
                    </Field>

                    <Field name="name_punjabi">
                      {({ field }) => (
                        <div>
                          <label className="flex items-center gap-2 mb-2 text-sm font-medium text-gray-700">
                            <span className="w-6 h-6 bg-purple-100 rounded flex items-center justify-center text-xs font-bold text-purple-600">PA</span>
                            Punjabi Name
                          </label>
                          <input
                            {...field}
                            type="text"
                            placeholder="ਪੰਜਾਬੀ ਵਿੱਚ ਅਹੁਦੇ ਦਾ ਨਾਮ ਦਰਜ ਕਰੋ"
                            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-pink-500 focus:border-pink-500 transition-all text-gray-700"
                          />
                          <ErrorMessage name="name_punjabi" component="div" className="mt-1 text-red-500 text-sm" />
                        </div>
                      )}
                    </Field>
                  </div>
                </div>

                <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-3">
                  <Link
                    to="/jobs_position"
                    className="px-6 py-2.5 border border-gray-200 rounded-xl text-gray-600 hover:bg-gray-100 transition-colors font-medium"
                  >
                    Cancel
                  </Link>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-6 py-2.5 bg-gradient-to-r from-pink-500 to-rose-500 text-white rounded-xl hover:from-pink-600 hover:to-rose-600 disabled:from-gray-300 disabled:to-gray-300 transition-all font-medium shadow-md flex items-center gap-2"
                  >
                    {isSubmitting ? (
                      <>
                        <i className="bi bi-arrow-repeat animate-spin"></i>
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-check-lg"></i>
                        Save Job Position
                      </>
                    )}
                  </button>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </main>
  );
};

export default LayoutHOC(CreateJobPosition);
